import React from 'react';
import { useTheme } from '../../context/ThemeContext';

const MUSCLE_TO_SVG = {
  pectorals:               ['f-chest-l', 'f-chest-r'],
  chest:                   ['f-chest-l', 'f-chest-r'],
  abs:                     ['f-abs'],
  'serratus anterior':     ['f-abs', 'f-chest-l', 'f-chest-r'],
  biceps:                  ['f-bicep-l', 'f-bicep-r'],
  brachialis:              ['f-bicep-l', 'f-bicep-r'],
  forearms:                ['f-forearm-l', 'f-forearm-r', 'b-forearm-l', 'b-forearm-r'],
  quads:                   ['f-quad-l', 'f-quad-r'],
  quadriceps:              ['f-quad-l', 'f-quad-r'],
  adductors:               ['f-inner-l', 'f-inner-r'],
  calves:                  ['f-calf-l', 'f-calf-r', 'b-calf-l', 'b-calf-r'],
  'tibialis anterior':     ['f-calf-l', 'f-calf-r'],
  delts:                   ['f-delt-l', 'f-delt-r', 'b-delt-l', 'b-delt-r'],
  shoulders:               ['f-delt-l', 'f-delt-r', 'b-delt-l', 'b-delt-r'],
  lats:                    ['b-lat-l', 'b-lat-r'],
  'upper back':            ['b-trap', 'b-lat-l', 'b-lat-r'],
  traps:                   ['b-trap'],
  rhomboids:               ['b-trap'],
  'lower back':            ['b-lower-back'],
  spine:                   ['b-lower-back'],
  glutes:                  ['b-glute-l', 'b-glute-r'],
  hamstrings:              ['b-ham-l', 'b-ham-r'],
  triceps:                 ['b-tri-l', 'b-tri-r'],
  obliques:                ['f-oblique-l', 'f-oblique-r'],
  'levator scapulae':      ['b-trap'],
  neck:                    ['f-neck'],
  'cardiovascular system': [],
};

function resolveIds(muscleNames = []) {
  return new Set(muscleNames.flatMap(m => MUSCLE_TO_SVG[m?.toLowerCase()] || []));
}

const SILHOUETTE = '#334155';
const EDGE = 'rgba(255,255,255,0.07)';

export default function MuscleMap({ target, secondaryMuscles = [] }) {
  const { theme } = useTheme();

  const primaryIds   = resolveIds([target]);
  const secondaryIds = new Set([...resolveIds(secondaryMuscles)].filter(id => !primaryIds.has(id)));

  const fill = (id) => {
    if (primaryIds.has(id))   return theme.accent;
    if (secondaryIds.has(id)) return theme.warning;
    return '#4a5568';
  };

  const op = (id) => (primaryIds.has(id) || secondaryIds.has(id)) ? 0.95 : 0.28;

  const glow = (id) => {
    if (primaryIds.has(id))   return `drop-shadow(0 0 6px ${theme.accent}) drop-shadow(0 0 2px ${theme.accent})`;
    if (secondaryIds.has(id)) return `drop-shadow(0 0 5px ${theme.warning})`;
    return 'none';
  };

  // Convenience: props to spread onto a <path> or <g>
  const mp = (id) => ({ fill: fill(id), opacity: op(id), style: { filter: glow(id) }, stroke: EDGE, strokeWidth: 0.4 });
  const gp = (id) => ({ fill: fill(id), opacity: op(id), style: { filter: glow(id) } });

  const FrontSVG = () => (
    <svg viewBox="0 0 100 242" width="108" height="242" style={{ display: 'block' }}>
      {/* ── Silhouette ── */}
      <circle cx="50" cy="13" r="10.5" fill={SILHOUETTE} opacity="0.5" />
      {/* neck */}
      <path d="M 45,23 L 45,31 Q 50,33 55,31 L 55,23 Q 50,21 45,23 Z" fill={SILHOUETTE} opacity="0.4" />
      {/* torso */}
      <path d="M 28,34 C 22,36 20,44 20,52 L 20,62 C 22,70 26,76 29,84 C 31,90 33,98 34,106 L 66,106 C 67,98 69,90 71,84 C 74,76 78,70 80,62 L 80,52 C 80,44 78,36 72,34 C 66,32 60,30 50,30 C 40,30 34,32 28,34 Z" fill={SILHOUETTE} opacity="0.18" />
      {/* hips */}
      <path d="M 34,106 C 29,108 26,114 27,121 L 73,121 C 74,114 71,108 66,106 Z" fill={SILHOUETTE} opacity="0.18" />
      {/* left arm */}
      <path d="M 17,35 C 12,38 10,46 11,55 C 12,63 14,72 16,78 C 18,84 20,90 21,96 L 21,104 C 21,106 23,107 25,107 C 27,107 28,106 28,104 L 28,95 C 27,89 25,83 23,77 C 21,70 20,62 20,54 C 20,47 22,39 26,36 C 23,34 19,33 17,35 Z" fill={SILHOUETTE} opacity="0.18" />
      {/* right arm */}
      <path d="M 83,35 C 88,38 90,46 89,55 C 88,63 86,72 84,78 C 82,84 80,90 79,96 L 79,104 C 79,106 77,107 75,107 C 73,107 72,106 72,104 L 72,95 C 73,89 75,83 77,77 C 79,70 80,62 80,54 C 80,47 78,39 74,36 C 77,34 81,33 83,35 Z" fill={SILHOUETTE} opacity="0.18" />
      {/* left leg */}
      <path d="M 27,121 C 25,126 25,134 26,143 C 27,153 29,162 30,168 C 31,174 31,182 31,190 C 31,198 31,206 32,213 L 46,213 C 45,205 45,197 45,189 C 45,181 46,173 47,167 C 48,161 49,151 49,141 C 49,131 48,122 46,121 Z" fill={SILHOUETTE} opacity="0.18" />
      {/* right leg */}
      <path d="M 73,121 C 75,126 75,134 74,143 C 73,153 71,162 70,168 C 69,174 69,182 69,190 C 69,198 69,206 68,213 L 54,213 C 55,205 55,197 55,189 C 55,181 54,173 53,167 C 52,161 51,151 51,141 C 51,131 52,122 54,121 Z" fill={SILHOUETTE} opacity="0.18" />

      {/* ── Muscles ── */}
      {/* Neck */}
      <path id="f-neck" d="M 45,23 C 44,26 44,29 45,31 Q 50,33 55,31 C 56,29 56,26 55,23 Q 50,21 45,23 Z" {...mp('f-neck')} />

      {/* Anterior deltoid L */}
      <path id="f-delt-l" d="M 26,35 C 19,33 14,38 13,46 C 12,53 15,59 20,60 C 25,61 30,57 31,50 C 32,44 30,36 26,35 Z" {...mp('f-delt-l')} />
      {/* Anterior deltoid R */}
      <path id="f-delt-r" d="M 74,35 C 81,33 86,38 87,46 C 88,53 85,59 80,60 C 75,61 70,57 69,50 C 68,44 70,36 74,35 Z" {...mp('f-delt-r')} />

      {/* Pec L */}
      <path id="f-chest-l" d="M 43,38 C 37,36 27,41 23,51 C 21,57 23,65 29,67 C 35,69 42,67 45,62 C 47,58 46,50 43,38 Z" {...mp('f-chest-l')} />
      {/* Pec R */}
      <path id="f-chest-r" d="M 57,38 C 63,36 73,41 77,51 C 79,57 77,65 71,67 C 65,69 58,67 55,62 C 53,58 54,50 57,38 Z" {...mp('f-chest-r')} />

      {/* Abs — 6 segments */}
      <g {...gp('f-abs')}>
        <rect x="41.5" y="69" width="7"   height="6" rx="1.5" stroke={EDGE} strokeWidth="0.4" />
        <rect x="51.5" y="69" width="7"   height="6" rx="1.5" stroke={EDGE} strokeWidth="0.4" />
        <rect x="41.5" y="78" width="7"   height="6" rx="1.5" stroke={EDGE} strokeWidth="0.4" />
        <rect x="51.5" y="78" width="7"   height="6" rx="1.5" stroke={EDGE} strokeWidth="0.4" />
        <rect x="41.5" y="87" width="7"   height="6" rx="1.5" stroke={EDGE} strokeWidth="0.4" />
        <rect x="51.5" y="87" width="7"   height="6" rx="1.5" stroke={EDGE} strokeWidth="0.4" />
      </g>

      {/* Oblique L */}
      <path id="f-oblique-l" d="M 34,67 C 30,71 28,80 29,91 C 30,99 34,105 38,103 C 41,99 40,91 38,81 C 36,73 36,68 34,67 Z" {...mp('f-oblique-l')} />
      {/* Oblique R */}
      <path id="f-oblique-r" d="M 66,67 C 70,71 72,80 71,91 C 70,99 66,105 62,103 C 59,99 60,91 62,81 C 64,73 64,68 66,67 Z" {...mp('f-oblique-r')} />

      {/* Bicep L */}
      <path id="f-bicep-l" d="M 20,48 C 15,48 12,56 13,64 C 14,70 18,74 22,73 C 26,72 28,65 27,58 C 26,52 24,46 20,48 Z" {...mp('f-bicep-l')} />
      {/* Bicep R */}
      <path id="f-bicep-r" d="M 80,48 C 85,48 88,56 87,64 C 86,70 82,74 78,73 C 74,72 72,65 73,58 C 74,52 76,46 80,48 Z" {...mp('f-bicep-r')} />

      {/* Forearm L */}
      <path id="f-forearm-l" d="M 17,75 C 13,76 10,84 11,94 C 12,101 16,105 20,104 C 24,103 26,96 25,87 C 24,80 21,73 17,75 Z" {...mp('f-forearm-l')} />
      {/* Forearm R */}
      <path id="f-forearm-r" d="M 83,75 C 87,76 90,84 89,94 C 88,101 84,105 80,104 C 76,103 74,96 75,87 C 76,80 79,73 83,75 Z" {...mp('f-forearm-r')} />

      {/* Quad L */}
      <path id="f-quad-l" d="M 34,122 C 28,122 26,135 27,148 C 28,160 33,167 39,166 C 45,165 48,157 47,145 C 46,133 43,120 34,122 Z" {...mp('f-quad-l')} />
      {/* Quad R */}
      <path id="f-quad-r" d="M 66,122 C 72,122 74,135 73,148 C 72,160 67,167 61,166 C 55,165 52,157 53,145 C 54,133 57,120 66,122 Z" {...mp('f-quad-r')} />

      {/* Inner thigh L */}
      <path id="f-inner-l" d="M 44,122 C 42,126 41,138 42,150 C 43,158 46,164 50,163 C 52,159 51,150 49,138 C 47,128 46,122 44,122 Z" {...mp('f-inner-l')} />
      {/* Inner thigh R */}
      <path id="f-inner-r" d="M 56,122 C 58,126 59,138 58,150 C 57,158 54,164 50,163 C 48,159 49,150 51,138 C 53,128 54,122 56,122 Z" {...mp('f-inner-r')} />

      {/* Calf front L (tibialis anterior) */}
      <path id="f-calf-l" d="M 33,171 C 29,173 27,183 28,196 C 29,205 33,210 37,209 C 41,208 43,200 42,189 C 41,180 36,169 33,171 Z" {...mp('f-calf-l')} />
      {/* Calf front R */}
      <path id="f-calf-r" d="M 67,171 C 71,173 73,183 72,196 C 71,205 67,210 63,209 C 59,208 57,200 58,189 C 59,180 64,169 67,171 Z" {...mp('f-calf-r')} />
    </svg>
  );

  const BackSVG = () => (
    <svg viewBox="0 0 100 242" width="108" height="242" style={{ display: 'block' }}>
      {/* ── Silhouette (same layout as front) ── */}
      <circle cx="50" cy="13" r="10.5" fill={SILHOUETTE} opacity="0.5" />
      <path d="M 45,23 L 45,31 Q 50,33 55,31 L 55,23 Q 50,21 45,23 Z" fill={SILHOUETTE} opacity="0.4" />
      <path d="M 28,34 C 22,36 20,44 20,52 L 20,62 C 22,70 26,76 29,84 C 31,90 33,98 34,106 L 66,106 C 67,98 69,90 71,84 C 74,76 78,70 80,62 L 80,52 C 80,44 78,36 72,34 C 66,32 60,30 50,30 C 40,30 34,32 28,34 Z" fill={SILHOUETTE} opacity="0.18" />
      <path d="M 34,106 C 29,108 26,114 27,121 L 73,121 C 74,114 71,108 66,106 Z" fill={SILHOUETTE} opacity="0.18" />
      <path d="M 17,35 C 12,38 10,46 11,55 C 12,63 14,72 16,78 C 18,84 20,90 21,96 L 21,104 C 21,106 23,107 25,107 C 27,107 28,106 28,104 L 28,95 C 27,89 25,83 23,77 C 21,70 20,62 20,54 C 20,47 22,39 26,36 C 23,34 19,33 17,35 Z" fill={SILHOUETTE} opacity="0.18" />
      <path d="M 83,35 C 88,38 90,46 89,55 C 88,63 86,72 84,78 C 82,84 80,90 79,96 L 79,104 C 79,106 77,107 75,107 C 73,107 72,106 72,104 L 72,95 C 73,89 75,83 77,77 C 79,70 80,62 80,54 C 80,47 78,39 74,36 C 77,34 81,33 83,35 Z" fill={SILHOUETTE} opacity="0.18" />
      <path d="M 27,121 C 25,126 25,134 26,143 C 27,153 29,162 30,168 C 31,174 31,182 31,190 C 31,198 31,206 32,213 L 46,213 C 45,205 45,197 45,189 C 45,181 46,173 47,167 C 48,161 49,151 49,141 C 49,131 48,122 46,121 Z" fill={SILHOUETTE} opacity="0.18" />
      <path d="M 73,121 C 75,126 75,134 74,143 C 73,153 71,162 70,168 C 69,174 69,182 69,190 C 69,198 69,206 68,213 L 54,213 C 55,205 55,197 55,189 C 55,181 54,173 53,167 C 52,161 51,151 51,141 C 51,131 52,122 54,121 Z" fill={SILHOUETTE} opacity="0.18" />

      {/* ── Muscles ── */}
      {/* Traps — upper diamond */}
      <path id="b-trap" d="M 42,30 C 46,28 54,28 58,30 L 74,46 C 67,52 58,54 50,54 C 42,54 33,52 26,46 Z" {...mp('b-trap')} />

      {/* Rear delt L */}
      <path id="b-delt-l" d="M 26,35 C 19,33 14,38 13,46 C 12,53 15,59 20,60 C 25,61 30,57 31,50 C 32,44 30,36 26,35 Z" {...mp('b-delt-l')} />
      {/* Rear delt R */}
      <path id="b-delt-r" d="M 74,35 C 81,33 86,38 87,46 C 88,53 85,59 80,60 C 75,61 70,57 69,50 C 68,44 70,36 74,35 Z" {...mp('b-delt-r')} />

      {/* Lat L */}
      <path id="b-lat-l" d="M 29,54 C 23,58 19,68 21,82 C 23,92 29,98 35,94 C 39,90 39,80 37,70 C 35,62 31,52 29,54 Z" {...mp('b-lat-l')} />
      {/* Lat R */}
      <path id="b-lat-r" d="M 71,54 C 77,58 81,68 79,82 C 77,92 71,98 65,94 C 61,90 61,80 63,70 C 65,62 69,52 71,54 Z" {...mp('b-lat-r')} />

      {/* Lower back — erector spinae (two columns) */}
      <g {...gp('b-lower-back')}>
        <path d="M 44,80 C 42,83 41,91 42,99 C 43,105 46,107 48,105 C 49,101 49,93 48,87 C 47,83 46,79 44,80 Z" stroke={EDGE} strokeWidth="0.4" />
        <path d="M 56,80 C 58,83 59,91 58,99 C 57,105 54,107 52,105 C 51,101 51,93 52,87 C 53,83 54,79 56,80 Z" stroke={EDGE} strokeWidth="0.4" />
      </g>

      {/* Tricep L */}
      <path id="b-tri-l" d="M 20,48 C 15,48 12,56 13,64 C 14,70 18,74 22,73 C 26,72 28,65 27,58 C 26,52 24,46 20,48 Z" {...mp('b-tri-l')} />
      {/* Tricep R */}
      <path id="b-tri-r" d="M 80,48 C 85,48 88,56 87,64 C 86,70 82,74 78,73 C 74,72 72,65 73,58 C 74,52 76,46 80,48 Z" {...mp('b-tri-r')} />

      {/* Rear forearm L */}
      <path id="b-forearm-l" d="M 17,75 C 13,76 10,84 11,94 C 12,101 16,105 20,104 C 24,103 26,96 25,87 C 24,80 21,73 17,75 Z" {...mp('b-forearm-l')} />
      {/* Rear forearm R */}
      <path id="b-forearm-r" d="M 83,75 C 87,76 90,84 89,94 C 88,101 84,105 80,104 C 76,103 74,96 75,87 C 76,80 79,73 83,75 Z" {...mp('b-forearm-r')} />

      {/* Glute L */}
      <path id="b-glute-l" d="M 31,109 C 25,111 23,122 25,132 C 27,142 33,147 39,145 C 45,143 47,135 45,124 C 43,114 37,107 31,109 Z" {...mp('b-glute-l')} />
      {/* Glute R */}
      <path id="b-glute-r" d="M 69,109 C 75,111 77,122 75,132 C 73,142 67,147 61,145 C 55,143 53,135 55,124 C 57,114 63,107 69,109 Z" {...mp('b-glute-r')} />

      {/* Hamstring L */}
      <path id="b-ham-l" d="M 31,147 C 26,149 24,160 25,172 C 26,182 31,189 37,187 C 43,185 45,175 44,164 C 43,154 36,145 31,147 Z" {...mp('b-ham-l')} />
      {/* Hamstring R */}
      <path id="b-ham-r" d="M 69,147 C 74,149 76,160 75,172 C 74,182 69,189 63,187 C 57,185 55,175 56,164 C 57,154 64,145 69,147 Z" {...mp('b-ham-r')} />

      {/* Calf back L (gastrocnemius) */}
      <path id="b-calf-l" d="M 30,190 C 26,192 24,202 26,213 C 28,220 32,224 36,222 C 40,220 42,212 40,202 C 38,194 34,188 30,190 Z" {...mp('b-calf-l')} />
      {/* Calf back R */}
      <path id="b-calf-r" d="M 70,190 C 74,192 76,202 74,213 C 72,220 68,224 64,222 C 60,220 58,212 60,202 C 62,194 66,188 70,190 Z" {...mp('b-calf-r')} />
    </svg>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '10px', color: theme.textSecondary, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px' }}>Front</div>
          <FrontSVG />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '8px', paddingTop: '110px', fontSize: '11px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '3px', background: theme.accent, flexShrink: 0 }} />
            <span style={{ color: theme.textSecondary, whiteSpace: 'nowrap' }}>Primary</span>
          </div>
          {secondaryIds.size > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '3px', background: theme.warning, flexShrink: 0 }} />
              <span style={{ color: theme.textSecondary, whiteSpace: 'nowrap' }}>Secondary</span>
            </div>
          )}
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '10px', color: theme.textSecondary, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px' }}>Back</div>
          <BackSVG />
        </div>
      </div>
    </div>
  );
}
